/*
  Copyright Epic Games, Inc. All Rights Reserved.
*/

"use client";

import { Button, Divider, Toggle } from "@eds/react";
import { css } from "@panda/css";
import { Box } from "@panda/jsx";
import { useDirectoryContext } from "../core/Directory.context";

const rowStyles = css({
	display: "flex",
	flexWrap: "wrap",
	alignItems: "center",
	gap: "eds.dimension.12",
	width: "full",
	"& > button": {
		maxW: "full!",
		"& > div": {
			textOverflow: "ellipsis",
			overflow: "hidden",
		},
	},
});

const clearAllStyles = css({
	display: "flex",
	alignItems: "stretch",
	alignSelf: "stretch",
	gap: "eds.dimension.16",
});

type ActiveFilter = { paramKey: string; value: string; label: string };

/**
 * Removable chips for every value selected in the `combobox` filters, followed by a
 * "Clear all" button that resets those filters (Figma 698:14246). Removing a chip toggles the value off via
 * `toggleMultiValueFilter`, which also drops it from the URL. Renders nothing when
 * no values are selected.
 */
export function DirectoryActiveFilters(): React.JSX.Element | null {
	const { config, result } = useDirectoryContext();

	const active: ActiveFilter[] = [];
	for (const f of config.filters) {
		if (f.kind !== "combobox") continue;
		for (const value of result.state.multiValueFilters[f.paramKey] ?? []) {
			const option = f.options.find((o) => o.value === value);
			if (option) active.push({ paramKey: f.paramKey, value, label: option.label });
		}
	}

	if (active.length === 0) return null;

	const removeLabel = config.removeFilterLabel ?? ((label: string) => `Remove ${label}`);
	// Clears only the filters represented by chips — the search query is left alone.
	const clearAll = (): void => {
		new Set(active.map((a) => a.paramKey)).forEach((paramKey) => result.clearFilter(paramKey));
	};

	return (
		<Box className={rowStyles}>
			{active.map(({ paramKey, value, label }) => {
				const remove = (): void => result.toggleMultiValueFilter(paramKey, value);
				return (
					<Toggle
						key={`${paramKey}:${value}`}
						label={label}
						size="lg"
						removable
						removeLabel={removeLabel(label)}
						selected={false}
						onChange={remove}
						onRemove={remove}
					/>
				);
			})}
			<Box className={clearAllStyles}>
				<Divider vertical />
				<Button variant="solid" size="lg" onClick={clearAll}>
					{config.clearAllFiltersLabel ?? "Clear all"}
				</Button>
			</Box>
		</Box>
	);
}
